import type { inferRouterOutputs } from '@trpc/server';
import type { EntityConfig } from '@/components/UI/EntitySelectionPanel';
import type { StatusTone } from '@/components/UI/StatusBadge';
import type { AppRouter } from '@/types/appRouter';
import type { AeroOptimizationJobSummary } from '@/features/tradeLogicAuthoring/api';

type RouterOutputs = inferRouterOutputs<AppRouter>;

export type AeroStrategySummary = RouterOutputs['strategy']['fetchStrategies']['strategies'][number];
export type AeroResourceAccessItem = RouterOutputs['resourceAccess']['listAccessibleResources'][number];
export type AeroJobStatus = AeroOptimizationJobSummary['status'];
export type AeroJobEntityType = EntityConfig['entityType'];

export const AERO_ACTIVE_STATUSES: AeroJobStatus[] = ['pending', 'queued', 'running', 'cancelling'];
export const AERO_TERMINAL_STATUSES: AeroJobStatus[] = ['completed', 'failed', 'cancelled'];
// Jobs through #77 completed before exact fact-backed portfolio metrics were deployed.
export const AERO_LAST_LEGACY_PORTFOLIO_METRICS_JOB_ID = 77;

export const parsePositiveInt = (value: string | null): number | null => {
    if (!value) return null;

    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed <= 0) return null;

    return parsed;
};

export const sortAeroStrategiesByName = (strategies: AeroStrategySummary[]) =>
    [...strategies].sort((left, right) => left.name.localeCompare(right.name) || left.id - right.id);

export const buildOwnedAeroStrategyIds = (accessItems: AeroResourceAccessItem[] | undefined) => {
    const ids = new Set<number>();

    for (const item of accessItems ?? []) {
        if (item.ownedByUser) ids.add(item.resource.resourcePk);
    }

    return ids;
};

export const buildOwnedAeroStrategies = ({
    isAccessLoading,
    ownedStrategyIds,
    strategies
}: {
    isAccessLoading: boolean;
    ownedStrategyIds: Set<number>;
    strategies: AeroStrategySummary[] | undefined;
}) => {
    if (isAccessLoading) return [];

    return sortAeroStrategiesByName((strategies ?? []).filter((strategy) => ownedStrategyIds.has(strategy.id)));
};

export const resolveAeroStrategy = ({
    fallbackToFirst,
    strategies,
    strategyId
}: {
    fallbackToFirst: boolean;
    strategies: AeroStrategySummary[];
    strategyId: number | null;
}) => {
    if (strategies.length === 0) return null;
    if (strategyId === null) return fallbackToFirst ? (strategies[0] ?? null) : null;

    return strategies.find((strategy) => strategy.id === strategyId) ?? null;
};

export const hasPinnedAeroExitVersion = (strategy: AeroStrategySummary | null) =>
    strategy?.tradeLogicV2ExitVersionId !== null && strategy?.tradeLogicV2ExitVersionId !== undefined;

export const isAeroJobActive = (job: Pick<AeroOptimizationJobSummary, 'status'>) => AERO_ACTIVE_STATUSES.includes(job.status);

export const isAeroJobTerminal = (job: Pick<AeroOptimizationJobSummary, 'status'>) => AERO_TERMINAL_STATUSES.includes(job.status);

export const usesLegacyAeroPortfolioMetrics = (job: Pick<AeroOptimizationJobSummary, 'id'>) =>
    job.id <= AERO_LAST_LEGACY_PORTFOLIO_METRICS_JOB_ID;

export const getAeroStatusTone = (status: AeroJobStatus): StatusTone => {
    if (status === 'completed') return 'success';
    if (status === 'failed' || status === 'cancelled') return 'danger';
    if (status === 'cancelling') return 'warning';
    if (status === 'running') return 'info';

    return 'neutral';
};

export const formatAeroStatus = (status: AeroJobStatus) =>
    status
        .split('_')
        .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`)
        .join(' ');

export const formatAeroCompactNumber = (value: number | null | undefined) => {
    if (value == null || Number.isNaN(value)) return 'None';

    return new Intl.NumberFormat().format(value);
};

export const formatAeroMetricNumber = (value: number | null | undefined) => {
    if (value == null || Number.isNaN(value)) return 'None';
    if (Number.isInteger(value)) return new Intl.NumberFormat().format(value);

    return value.toFixed(2);
};

export const formatAeroPercentage = (value: number | null | undefined) => {
    if (value == null || Number.isNaN(value)) return 'None';

    return `${formatAeroMetricNumber(value)}%`;
};

export const formatAeroShortHash = (hash: string) => (hash.length <= 18 ? hash : `${hash.slice(0, 12)}...${hash.slice(-4)}`);

export const formatAeroJobDisplayName = (job: AeroOptimizationJobSummary) => job.name?.trim() || `AERO Job #${job.id}`;

export const formatSelectedAeroEntitiesAsRefs = (entities: EntityConfig[]) =>
    entities.map((entity) => `${entity.entityType}:${entity.entityId}`).join('\n');
